import { useState, useCallback } from 'react';
import type { IFormItem, IFormSection } from '@/@types/claims';

export type { IFormItem, IFormSection } from '@/@types/claims';

function makeId(): string {
  return Math.random().toString(36).slice(2);
}

function makeItem(sortOrder: number): IFormItem {
  return {
    tempId: makeId(),
    name: '',
    description: '',
    planPaysPercent: '100',
    youPayPercent: '0',
    annualLimit: '',
    lifetimeLimit: '',
    visitLimit: '',
    waitingPeriodMonths: '',
    cashBenefitPerUnit: '',
    cashBenefitUnit: '',
    cashBenefitMaxUnits: '',
    sortOrder,
  };
}

export function useBenefitPlanForm() {
  const [sections, setSections] = useState<IFormSection[]>([]);

  const addSection = useCallback(() => {
    setSections(prev => [
      ...prev,
      { tempId: makeId(), name: '', sortOrder: prev.length, items: [] },
    ]);
  }, []);

  const updateSection = useCallback((tempId: string, name: string) => {
    setSections(prev =>
      prev.map(s => (s.tempId === tempId ? { ...s, name } : s))
    );
  }, []);

  const removeSection = useCallback((tempId: string) => {
    setSections(prev => prev.filter(s => s.tempId !== tempId));
  }, []);

  const moveSectionUp = useCallback((tempId: string) => {
    setSections(prev => {
      const idx = prev.findIndex(s => s.tempId === tempId);
      if (idx <= 0) return prev;
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next.map((s, i) => ({ ...s, sortOrder: i }));
    });
  }, []);

  const moveSectionDown = useCallback((tempId: string) => {
    setSections(prev => {
      const idx = prev.findIndex(s => s.tempId === tempId);
      if (idx < 0 || idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next.map((s, i) => ({ ...s, sortOrder: i }));
    });
  }, []);

  const addItem = useCallback((sectionTempId: string) => {
    setSections(prev =>
      prev.map(s =>
        s.tempId === sectionTempId
          ? { ...s, items: [...s.items, makeItem(s.items.length)] }
          : s
      )
    );
  }, []);

  const updateItem = useCallback(
    (sectionTempId: string, itemTempId: string, patch: Partial<IFormItem>) => {
      setSections(prev =>
        prev.map(s =>
          s.tempId === sectionTempId
            ? {
                ...s,
                items: s.items.map(it =>
                  it.tempId === itemTempId ? { ...it, ...patch } : it
                ),
              }
            : s
        )
      );
    },
    []
  );

  const removeItem = useCallback((sectionTempId: string, itemTempId: string) => {
    setSections(prev =>
      prev.map(s =>
        s.tempId === sectionTempId
          ? { ...s, items: s.items.filter(it => it.tempId !== itemTempId) }
          : s
      )
    );
  }, []);

  const moveItemUp = useCallback((sectionTempId: string, itemTempId: string) => {
    setSections(prev =>
      prev.map(s => {
        if (s.tempId !== sectionTempId) return s;
        const idx = s.items.findIndex(it => it.tempId === itemTempId);
        if (idx <= 0) return s;
        const items = [...s.items];
        [items[idx - 1], items[idx]] = [items[idx], items[idx - 1]];
        return { ...s, items: items.map((it, i) => ({ ...it, sortOrder: i })) };
      })
    );
  }, []);

  const moveItemDown = useCallback((sectionTempId: string, itemTempId: string) => {
    setSections(prev =>
      prev.map(s => {
        if (s.tempId !== sectionTempId) return s;
        const idx = s.items.findIndex(it => it.tempId === itemTempId);
        if (idx < 0 || idx >= s.items.length - 1) return s;
        const items = [...s.items];
        [items[idx], items[idx + 1]] = [items[idx + 1], items[idx]];
        return { ...s, items: items.map((it, i) => ({ ...it, sortOrder: i })) };
      })
    );
  }, []);

  const loadSections = useCallback((incoming: IFormSection[]) => {
    setSections(incoming);
  }, []);

  return {
    sections,
    addSection,
    updateSection,
    removeSection,
    moveSectionUp,
    moveSectionDown,
    addItem,
    updateItem,
    removeItem,
    moveItemUp,
    moveItemDown,
    loadSections,
  };
}
